import { LatLng, LatLngBounds, Map } from 'leaflet';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer as LeafletMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Analytics from '../../lib/user-analytics';
import { actionMapLoaded } from '../../store/App/actions';
import { selectFlyToMapPosition } from '../../store/App/selectors';
import {
    selectBeingMobileSentinelSearch,
    selectIsMobileInteractionEnabled,
    selectShowMobileLoadingSpinner,
} from '../../store/Map/Mobile/selectors';
import { actionSentinelMapZoom, actionSentinelSelectAOI } from '../../store/Map/Sentinel/actions';
import SatelliteProviders from '../MapView/SatelliteProviders/satellite-providers';
import SatelliteLayers from '../MapView/SatelliteTracker/satellite-layers';
import SentinelLayers from '../MapView/Sentinel/sentinel-layers';
import TileLayers from '../MapView/TileLayers/tilelayers';
import { SubmittingSpinner } from '../Shared/submitting-spinner';
import MobileBasemap from './BaseMap/mobile-basemap';
import SearchResultMarker from '../MapView/SearchResultMarker/search-result-marker';

// Values that give the map an aesthetically pleasing start position
const WORLD_ZOOM = 1.5;
const MAX_ZOOM_LEVEL = 28;
const WORLD_CENTER: [number, number] = [34.5, 0];

// Level to zoom to when focusing on a point
const FLYTO_ZOOM_LEVEL = 11;

// Force the map back to these bounds while panning
const WORLD_DRAG_REBOUND_BOUNDS: [number, number][] = [
    [-80.98155760646617, -220],
    [89.99346179538875, 240],
];

const MobileMap = () => {
    const isMobileMapInteractionEnabled = useSelector(selectIsMobileInteractionEnabled);
    const beginMobileSentinelSearch = useSelector(selectBeingMobileSentinelSearch);
    const mapPosition = useSelector(selectFlyToMapPosition);
    const showMobileLoadingSpinner = useSelector(selectShowMobileLoadingSpinner);

    const mapRef = useRef<Map>(null);

    const dispatch = useDispatch();

    const [mapLoaded, setMapLoaded] = useState(false);

    const flyTo = useCallback(
        (position: LatLng | LatLngBounds) => {
            if (!mapRef?.current) return;
            if (position instanceof LatLng) {
                const params = new URLSearchParams(window.location.search);
                const zoom = parseInt(params.get('pos')?.split(',')[2] || '');
                if (zoom && !Number.isNaN(zoom)) {
                    mapRef.current.flyTo(position, zoom);
                } else {
                    mapRef.current.flyTo(position, FLYTO_ZOOM_LEVEL);
                }
            } else if (position instanceof LatLngBounds) {
                mapRef.current.flyToBounds(position);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mapRef?.current]
    );

    useEffect(() => {
        const setSentinelAOI = (bbox: LatLngBounds) => dispatch(actionSentinelSelectAOI(bbox));

        if (beginMobileSentinelSearch && mapRef?.current) {
            setSentinelAOI(mapRef.current.getBounds());
            Analytics.Event('Mobile Map', 'Created sentinel AOI', mapRef.current.getBounds());
            Analytics.Event('Mobile - Map', 'Created sentinel AOI', mapRef.current.getBounds());
        }
    }, [beginMobileSentinelSearch, dispatch]);

    // Prevent the map from wrapping by forcing in to pan inside world bounds
    useEffect(() => {
        const setMapZoomLevel = (zoom: number) => dispatch(actionSentinelMapZoom(zoom));

        const map = mapRef.current;
        if (!map) return;
        map.on('drag', () => {
            map.panInsideBounds(WORLD_DRAG_REBOUND_BOUNDS, { animate: false });
        });

        map.on('zoomend', () => {
            map.panInsideBounds(WORLD_DRAG_REBOUND_BOUNDS, { animate: false });
            setMapZoomLevel(map.getZoom());
        });

        setMapLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, mapRef?.current]);

    useEffect(() => {
        const setActionMapLoaded = () => dispatch(actionMapLoaded());

        if (mapLoaded) {
            setActionMapLoaded();
        }
    }, [mapLoaded, dispatch]);

    useEffect(() => {
        if (mapPosition) {
            flyTo(mapPosition);
        }
    }, [flyTo, mapPosition]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (isMobileMapInteractionEnabled) {
            map.dragging.enable();
            map.touchZoom.enable();
            map.doubleClickZoom.enabled();
            map.scrollWheelZoom.enabled();
            map.boxZoom.enabled();
            map.keyboard.enabled();
        } else {
            map.dragging.disable();
            map.touchZoom.disable();
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileMapInteractionEnabled, mapRef?.current]);

    return (
        <MapContainer>
            <LeafletMap
                tap={false}
                zoom={WORLD_ZOOM}
                center={WORLD_CENTER}
                zoomControl={false}
                minZoom={WORLD_ZOOM}
                maxZoom={MAX_ZOOM_LEVEL}
                maxBounds={[
                    [-90, -180],
                    [90, 180],
                ]}
                maxBoundsViscosity={1.0}
                ref={mapRef}
            >
                <React.Fragment>
                    <MobileBasemap />
                    <SatelliteLayers />
                    <TileLayers editMode={false} />
                    <SentinelLayers />
                    {/*
                        TODO: Deal with mobile annotation
                        <Annotations hidden={true} />
                    */}
                    <SatelliteProviders />
                </React.Fragment>
            </LeafletMap>

            {showMobileLoadingSpinner ? (
                <MobileSpinnerContainer>
                    <SubmittingSpinner />
                </MobileSpinnerContainer>
            ) : null}
        </MapContainer>
    );
};

export default MobileMap;

const MobileSpinnerContainer = styled.div`
    position: absolute;
    left: 50vw;
    top: 38vh;
    width: 35px;
    height: 35px;
    z-index: 3000;
`;

const MapContainer = styled.div`
    width: 100vw;
    height: 100vh;
    position: fixed;
    background: black;
    /* overflow-y: hidden; */
    /* overscroll-behavior-y: none !important; */

    #kml-button,
    #box-select-button,
    #ruler-button,
    #text-field-button,
    #arrow-button,
    #circle-button,
    #rectangle-button,
    #latLngTool-button,
    #box-zoom-button,
    .leaflet-control-zoom-in,
    .leaflet-control-zoom-out,
    #export-button,
    #remove-all-button {
        visibility: hidden !important;
        width: 0 !important;
        margin: 0 !important;
    }
`;
